if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.elements?.viewer) return;
        if (this._connected) return;
        this._connected = true;

        this.initAutoplayObserver();
        this.preloadVideoMedia();

        window.setTimeout(() => {
          const activeItem =
            this.elements.viewer.querySelector('[data-media-id].is-active') ||
            this.elements.viewer.querySelector('[data-media-id]');
          if (activeItem) this.playActiveMedia(activeItem);
        }, 0);
      }

      preloadVideoMedia() {
        if (!this.elements?.viewer) return;
        if (this._preloaded) return;
        this._preloaded = true;

        const run = () => {
          const deferredMediaElements = this.elements.viewer.querySelectorAll(
            '.product-media-container.media-type-video .deferred-media, .product-media-container.media-type-external_video .deferred-media'
          );

          deferredMediaElements.forEach((deferredMedia) => {
            if (deferredMedia.getAttribute('loaded')) return;
            if (deferredMedia.dataset.piPreloadAttempted === 'true') return;
            deferredMedia.dataset.piPreloadAttempted = 'true';

            try {
              deferredMedia.loadContent(false);
            } catch (e) {}

            const video = deferredMedia.querySelector('video');
            if (!video) return;

            video.muted = true;
            video.setAttribute('muted', '');
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.setAttribute('webkit-playsinline', '');
            video.preload = 'auto';
            video.setAttribute('preload', 'auto');
            video.controls = false;
            video.removeAttribute('controls');

            this.setupTapToToggle(video, deferredMedia);

            try {
              video.pause();
            } catch (e) {}
          });
        };

        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(run, { timeout: 1500 });
        } else {
          window.setTimeout(run, 200);
        }
      }

      initAutoplayObserver() {
        if (this._autoplayObserver) return;
        if (!('IntersectionObserver' in window)) return;

        this._autoplayObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const deferredMedia = entry.target;
              if (entry.isIntersecting) {
                this.playMediaElement(deferredMedia);
              } else {
                this.pauseMediaElement(deferredMedia);
              }
            });
          },
          { threshold: 0.4 }
        );

        const videoElements = this.elements.viewer.querySelectorAll('.deferred-media[data-pi-video]');
        videoElements.forEach((el) => this._autoplayObserver.observe(el));
      }

      playMediaElement(deferredMedia) {
        if (!deferredMedia) return;

        if (!deferredMedia.getAttribute('loaded')) {
          deferredMedia.loadContent(false);
        }

        const video = deferredMedia.querySelector('video');
        if (video) {
          this.setupAndPlayVideo(video, deferredMedia);
        } else {
          // If it's an iframe, we rely on the autoplay parameter in the URL
          // But we can still try to find the video element after a short delay
          // in case it's a Shopify video that hasn't been appended yet.
          window.setTimeout(() => {
            const v = deferredMedia.querySelector('video');
            if (v) this.setupAndPlayVideo(v, deferredMedia);
          }, 100);
        }
      }

      pauseMediaElement(deferredMedia) {
        if (!deferredMedia) return;
        const video = deferredMedia.querySelector('video');
        if (video && !video.paused) {
          video.pause();
        }
      }

      setupAndPlayVideo(video, deferredMedia) {
        if (!video) return;

        video.muted = true;
        video.setAttribute('muted', '');
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.controls = false;
        video.removeAttribute('controls');

        this.setupTapToToggle(video, deferredMedia);
        this.startVideoPlayback(video);
      }

      onSlideChanged(event) {
        const currentElement = event?.detail?.currentElement;
        if (!currentElement) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        currentElement.classList.add('is-active');
        this.playActiveMedia(currentElement);

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${currentElement.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;
        this.playMediaElement(deferredMedia);
      }

      setupTapToToggle(video, deferredMedia) {
        if (!video || !deferredMedia) return;
        if (video.dataset.piTapToggleBound === 'true') return;
        video.dataset.piTapToggleBound = 'true';

        const toggleButton =
          deferredMedia.querySelector('[data-pi-video-toggle]') || this.createTapToggleButton(deferredMedia);
        const setState = (state) => {
          if (!toggleButton) return;
          toggleButton.dataset.piVideoState = state;
          toggleButton.setAttribute('data-pi-video-state', state);
          toggleButton.setAttribute('aria-label', state === 'paused' ? 'Play video' : 'Pause video');
          toggleButton.setAttribute('aria-pressed', state === 'paused' ? 'true' : 'false');
        };

        const showOverlay = (persist) => {
          if (!toggleButton) return;
          deferredMedia.classList.add('pi-video-toggle-visible');
          if (persist) return;
          window.clearTimeout(toggleButton._piHideTimer);
          toggleButton._piHideTimer = window.setTimeout(() => {
            if (video.paused) return;
            deferredMedia.classList.remove('pi-video-toggle-visible');
          }, 900);
        };

        const syncFromVideo = () => {
          if (video.paused) {
            setState('paused');
            showOverlay(true);
          } else {
            setState('playing');
            deferredMedia.classList.remove('pi-video-toggle-visible');
          }
        };

        const togglePlayback = (event) => {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }

          if (video.paused) {
            try {
              const p = video.play();
              if (p?.catch) p.catch(() => {});
            } catch (e) {}
            setState('playing');
            showOverlay(false);
          } else {
            try {
              video.pause();
            } catch (e) {}
            setState('paused');
            showOverlay(true);
          }
        };

        if (toggleButton) {
          toggleButton.addEventListener('click', togglePlayback);
        }

        video.addEventListener('click', togglePlayback);
        video.addEventListener('touchend', togglePlayback, { passive: false });
        video.addEventListener('play', syncFromVideo);
        video.addEventListener('pause', syncFromVideo);

        syncFromVideo();
      }

      createTapToggleButton(deferredMedia) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pi-video-toggle';
        button.setAttribute('data-pi-video-toggle', '');
        button.setAttribute('data-pi-video-state', 'playing');
        button.setAttribute('aria-label', 'Pause video');
        deferredMedia.appendChild(button);
        return button;
      }

      startVideoPlayback(video) {
        if (!video) return;

        video.preload = 'auto';
        video.setAttribute('preload', 'auto');

        const attemptKey = 'piAutoplayAttempts';
        const attempts = parseInt(video.dataset[attemptKey] || '0', 10);
        video.dataset[attemptKey] = String(attempts + 1);

        const attemptPlay = () => {
          try {
            const playPromise = video.play();
            if (playPromise?.catch) playPromise.catch(() => {});
          } catch (e) {}
        };

        const ensurePlaying = () => {
          if (!video.isConnected) return;
          if (!video.paused && video.readyState >= 3) return;

          try {
            if (!Number.isNaN(video.duration) && video.duration > 0) {
              const targetTime = Math.min(0.01, Math.max(0, video.duration - 0.01));
              if (video.currentTime < targetTime) video.currentTime = targetTime;
            }
          } catch (e) {}

          try {
            video.pause();
          } catch (e) {}

          attemptPlay();
        };

        video.addEventListener('canplay', ensurePlaying, { once: true });
        video.addEventListener('loadeddata', ensurePlaying, { once: true });

        requestAnimationFrame(() => {
          attemptPlay();
          window.setTimeout(ensurePlaying, 350);
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
